import {
  createPanelFrame,
  resolveTitle,
  createMetric,
  formatNumber,
  formatPercent,
  toNumber,
  clamp
} from './panel-utils.js';

export function render({ node, api }) {
  const { root, body, titleEl, metaEl } = createPanelFrame(node, 'Particle Timeseries');

  const metrics = document.createElement('div');
  metrics.className = 'ew-metric-grid';

  const canvasWrap = document.createElement('div');
  canvasWrap.className = 'ew-particle-canvas';
  const canvas = document.createElement('canvas');
  canvas.width = 360;
  canvas.height = 160;
  canvasWrap.appendChild(canvas);

  const footer = document.createElement('div');
  footer.className = 'ew-particle-footer';

  body.append(metrics, canvasWrap, footer);

  const ctx = canvas.getContext('2d');
  const state = {
    frame: 0,
    running: true,
    particles: [],
    lastTs: 0,
    raf: null,
    resizeObserver: null
  };

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Particle Timeseries');

    const data = normalizeData(next);
    const latest = data.series[data.series.length - 1];
    metaEl.textContent = data.window || '24h';

    metrics.innerHTML = '';
    metrics.append(
      createMetric('current', formatNumber(latest?.v ?? 0, 2)),
      createMetric('avg', formatNumber(data.metrics.avg, 2)),
      createMetric('max', formatNumber(data.metrics.max, 2)),
      createMetric('drift', formatPercent(data.metrics.drift, 0))
    );

    footer.textContent = data.mode === 'alert' ? 'Alert mode' : 'Spectrum mode';

    state.data = data;
    ensureParticles(state);
    startLoop();
  };

  const startLoop = () => {
    if (state.raf) return;
    const tick = (ts) => {
      state.raf = requestAnimationFrame(tick);
      const delta = state.lastTs ? ts - state.lastTs : 16;
      state.lastTs = ts;
      drawFrame(state, ctx, delta);
    };
    state.raf = requestAnimationFrame(tick);
  };

  const stopLoop = () => {
    if (state.raf) cancelAnimationFrame(state.raf);
    state.raf = null;
  };

  state.resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const { width, height } = entry.contentRect;
      canvas.width = Math.max(200, Math.floor(width));
      canvas.height = Math.max(120, Math.floor(height));
    }
  });
  state.resizeObserver.observe(canvasWrap);

  update(node);

  return {
    el: root,
    update,
    cleanup: () => {
      stopLoop();
      state.resizeObserver?.disconnect();
    }
  };
}

function normalizeData(node) {
  const raw = node?.data || node?.props || {};
  const series = Array.isArray(raw.series) && raw.series.length
    ? raw.series.map((d, i) => ({ t: toNumber(d.t ?? i, i), v: toNumber(d.v, 0) }))
    : defaultSeries();

  const max = Math.max(...series.map((d) => d.v));
  const min = Math.min(...series.map((d) => d.v));
  const avg = series.reduce((acc, d) => acc + d.v, 0) / series.length;
  const drift = toNumber(raw.metrics?.drift, 0.18);

  return {
    series,
    window: raw.window || '24h',
    metrics: {
      avg: toNumber(raw.metrics?.avg, avg),
      max: toNumber(raw.metrics?.max, max),
      min: toNumber(raw.metrics?.min, min),
      drift
    },
    anomalies: Array.isArray(raw.anomalies) ? raw.anomalies : [],
    mode: raw.mode || 'spectrum',
    particleCount: clamp(toNumber(raw.particleCount, 600), 200, 1200),
    turbulence: clamp(toNumber(raw.turbulence, 0.4), 0, 1),
    smoothing: clamp(toNumber(raw.smoothing, 0.2), 0, 1)
  };
}

function defaultSeries() {
  return Array.from({ length: 120 }, (_, i) => ({
    t: i,
    v: 40 + Math.sin(i * 0.15) * 10 + Math.cos(i * 0.05) * 6
  }));
}

function ensureParticles(state) {
  const target = state.data?.particleCount || 600;
  while (state.particles.length < target) state.particles.push(spawnParticle(state));
  if (state.particles.length > target) state.particles.length = target;
}

function spawnParticle(state) {
  return {
    x: Math.random(),
    y: Math.random(),
    life: Math.random(),
    speed: 0.2 + Math.random() * 0.8
  };
}

function drawFrame(state, ctx, delta) {
  if (!state.data) return;
  const { series, metrics, anomalies, mode, turbulence } = state.data;
  const width = ctx.canvas.width;
  const height = ctx.canvas.height;
  ctx.clearRect(0, 0, width, height);

  ctx.fillStyle = 'rgba(5, 18, 30, 0.85)';
  ctx.fillRect(0, 0, width, height);

  drawGrid(ctx, width, height);

  const min = metrics.min;
  const max = metrics.max === min ? min + 1 : metrics.max;

  const points = series.map((d, i) => {
    const t = i / (series.length - 1 || 1);
    const y = 1 - (d.v - min) / (max - min);
    return { t, y: clamp(y, 0, 1), v: d.v };
  });

  state.particles.forEach((p) => {
    p.x += p.speed * (delta / 1000) * 0.2;
    if (p.x > 1) {
      p.x = 0;
      p.y = Math.random();
      p.speed = 0.2 + Math.random() * 0.8;
    }
    const sample = sampleSeries(points, p.x);
    const targetY = sample.y + (Math.random() - 0.5) * turbulence * 0.25;
    p.y = lerp(p.y, targetY, 0.08);
    const px = p.x * width;
    const py = p.y * height;

    const intensity = clamp((sample.v - min) / (max - min), 0, 1);
    const color = mode === 'alert'
      ? `rgba(255, ${Math.floor(110 + intensity * 80)}, 110, ${0.2 + intensity * 0.6})`
      : `rgba(${Math.floor(80 + intensity * 100)}, ${Math.floor(200 + intensity * 30)}, 255, ${0.25 + intensity * 0.5})`;

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(px, py, 1.2 + intensity * 1.5, 0, Math.PI * 2);
    ctx.fill();
  });

  drawPulse(ctx, width, height, points, anomalies);
}

function drawGrid(ctx, width, height) {
  ctx.strokeStyle = 'rgba(95, 234, 255, 0.08)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  const step = height / 4;
  for (let i = 1; i < 4; i += 1) {
    ctx.moveTo(0, step * i);
    ctx.lineTo(width, step * i);
  }
  ctx.stroke();
}

function drawPulse(ctx, width, height, points, anomalies) {
  if (!anomalies || !anomalies.length) return;
  anomalies.forEach((anomaly) => {
    const idx = clamp(Math.floor(((anomaly.t ?? 0) / (points.length - 1 || 1)) * points.length), 0, points.length - 1);
    const point = points[idx];
    if (!point) return;
    const x = point.t * width;
    const y = point.y * height;
    const r = 6 + (Math.sin(Date.now() / 200) + 1) * 6;
    ctx.strokeStyle = 'rgba(255, 110, 110, 0.5)';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();
  });
}

function sampleSeries(points, t) {
  if (!points.length) return { y: 0.5, v: 0 };
  const idx = t * (points.length - 1);
  const left = Math.floor(idx);
  const right = Math.min(points.length - 1, left + 1);
  const frac = idx - left;
  const a = points[left];
  const b = points[right];
  return { y: lerp(a.y, b.y, frac), v: lerp(a.v, b.v, frac) };
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}
